import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, Eye, MessageSquare, ArrowLeft } from 'lucide-react';
import { getErrorMessage } from '../utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewDashboard = () => {
  const navigate = useNavigate();
  const [pendingOpinions, setpendingOpinions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOpinion, setSelectedOpinion] = useState(null);
  const [reviewAction, setReviewAction] = useState(''); // 'approve' or 'reject'
  const [newComment, setNewComment] = useState('');
  const [existingComments, setExistingComments] = useState([]);
  const [reviewerName, setReviewerName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [uploadingReviewerDoc, setUploadingReviewerDoc] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setReviewerName(user.full_name || user.username || 'Reviewer');
    fetchPendingOpinions();
  }, []);

  const fetchPendingOpinions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/opinions/pending-review`);
      setpendingOpinions(response.data.opinions || []);
    } catch (error) {
      console.error('Error fetching pending opinions:', error);
      alert('Failed to fetch pending opinions');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (opinionId) => {
    try {
      const response = await axios.get(`${API}/opinions/${opinionId}/comments`);
      setExistingComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setExistingComments([]);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert('Please enter a comment');
      return;
    }

    try {
      setAddingComment(true);
      await axios.post(`${API}/opinions/${selectedOpinion.id}/comments`, {
        opinion_id: selectedOpinion.id,
        comment_text: newComment.trim()
      });

      alert('Comment added successfully!');
      setNewComment('');
      // Refresh comments
      await fetchComments(selectedOpinion.id);
    } catch (error) {
      console.error('Error adding comment:', error);
      const errorMsg = getErrorMessage(error, 'Failed to add comment');
      alert(errorMsg);
    } finally {
      setAddingComment(false);
    }
  };

  const handleDownloadDocument = async (opinionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/download/${opinionId}/docx`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Failed to download document');
        return;
      }

      // Create blob from response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `legal_opinion_${opinionId.substring(0, 8)}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const handleUploadReviewerDocument = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.docx')) {
      alert('Please upload a .docx file');
      return;
    }

    try {
      setUploadingReviewerDoc(true);
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/opinions/${selectedOpinion.id}/upload-reviewer-document`, formData, {
        headers: {
        }
      });

      alert('Commented document uploaded successfully! Advocates will download this version.');
    } catch (error) {
      console.error('Error uploading reviewer document:', error);
      const errorMsg = getErrorMessage(error, 'Failed to upload document');
      alert(errorMsg);
    } finally {
      setUploadingReviewerDoc(false);
    }
  };

  const handleReview = async () => {
    if (!reviewAction) {
      alert('Please select approve or reject');
      return;
    }

    // Check if at least one comment has been added
    if (existingComments.length === 0) {
      alert('Please add at least one comment before submitting your review');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/opinions/review`, {
        document_id: selectedOpinion.id,
        action: reviewAction,
        comments: null, // Comments are now in the comments array
        reviewer_name: reviewerName
      });

      alert(`Opinion ${reviewAction}d successfully!`);
      setSelectedOpinion(null);
      setReviewAction('');
      setNewComment('');
      setExistingComments([]);
      fetchPendingOpinions();
    } catch (error) {
      console.error('Error submitting review:', error);
      const errorMsg = getErrorMessage(error, 'Failed to submit review');
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Review Dashboard</h1>
            <p className="text-gray-600 mt-1">Review and approve legal opinions</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading pending opinions...</p>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Review</p>
                    <p className="text-3xl font-bold text-orange-600">{pendingOpinions.length}</p>
                  </div>
                  <Clock className="w-12 h-12 text-orange-400" />
                </div>
              </div>
            </div>

            {/* Pending Opinions List */}
            {pendingOpinions.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">All Caught Up!</h2>
                <p className="text-gray-600">No opinions pending review at the moment.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opinion ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bank Format</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pendingOpinions.map((opinion) => (
                      <tr key={opinion.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-mono">{opinion.id.substring(0, 8)}...</td>
                        <td className="px-6 py-4 text-sm">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                            {opinion.bank_format}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(opinion.submitted_for_review_at)}
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                            Pending Review
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={async () => {
                              setSelectedOpinion(opinion);
                              await fetchComments(opinion.id);
                            }}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            <span>Review</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Review Modal */}
        {selectedOpinion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Review Opinion</h2>
                
                {/* Opinion Details */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-2">
                  <p><strong>Opinion ID:</strong> {selectedOpinion.id}</p>
                  <p><strong>Bank Format:</strong> <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">{selectedOpinion.bank_format}</span></p>
                  <p><strong>Generated At:</strong> {formatDate(selectedOpinion.generated_at)}</p>
                  <p><strong>Submitted For Review:</strong> {formatDate(selectedOpinion.submitted_for_review_at)}</p>
                </div>

                {/* Download Document to Review */}
                <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className="font-semibold text-blue-900">Review Document</h3>
                    </div>
                  </div>
                  <p className="text-sm text-blue-800 mb-3">Download the document to review in MS Word before approving</p>
                  <button
                    onClick={() => handleDownloadDocument(selectedOpinion.id)}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Download Document</span>
                  </button>
                  <p className="text-xs text-blue-600 mt-2">💡 Tip: Use "Track Changes" in Word to suggest edits without changing the format</p>
                </div>

                {/* Upload Commented Document Section */}
                <div className="mb-6 p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
                  <div className="flex items-center space-x-2 mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <h3 className="font-semibold text-purple-900">Upload Commented Document (Optional)</h3>
                  </div>
                  <p className="text-sm text-purple-800 mb-3">
                    If you've added comments/annotations in Word, upload the commented document here. The advocate will download this version.
                  </p>
                  <div className="flex items-center space-x-3">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".docx"
                        onChange={handleUploadReviewerDocument}
                        className="hidden"
                        disabled={uploadingReviewerDoc}
                      />
                      <div className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span>{uploadingReviewerDoc ? 'Uploading...' : 'Upload Commented DOCX'}</span>
                      </div>
                    </label>
                  </div>
                  <p className="text-xs text-purple-600 mt-2">
                    📝 This step is optional. Use this if you want to provide detailed comments/annotations in the document itself.
                  </p>
                </div>

                {/* Comments History Section */}
                <div className="mb-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <MessageSquare className="w-5 h-5 text-gray-700" />
                    <h3 className="font-semibold text-gray-900">Review Comments ({existingComments.length})</h3>
                  </div>
                  
                  {/* Existing Comments */}
                  {existingComments.length > 0 ? (
                    <div className="max-h-60 overflow-y-auto space-y-3 mb-4 bg-gray-50 p-3 rounded-lg">
                      {existingComments.map((comment) => (
                        <div key={comment.id} className="bg-white p-3 rounded border border-gray-200">
                          <div className="flex justify-between items-start mb-2">
                            <span className="font-medium text-sm text-gray-900">{comment.reviewer_name}</span>
                            <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.comment_text}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
                      <p className="text-sm text-yellow-800">⚠️ No comments yet. Please add at least one comment before approving or rejecting.</p>
                    </div>
                  )}

                  {/* Add New Comment */}
                  <div className="bg-white border-2 border-gray-200 rounded-lg p-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Add Comment</label>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Enter your review comments here..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                      rows="3"
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || addingComment}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>{addingComment ? 'Adding...' : 'Add Comment'}</span>
                    </button>
                  </div>
                </div>

                {/* Review Action */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Decision</label>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setReviewAction('approve')}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 ${
                        reviewAction === 'approve'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 text-gray-700 hover:border-green-500'
                      }`}
                    >
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Approve</span>
                    </button>
                    <button
                      onClick={() => setReviewAction('reject')}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 ${
                        reviewAction === 'reject'
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-300 text-gray-700 hover:border-red-500'
                      }`}
                    >
                      <XCircle className="w-5 h-5" />
                      <span className="font-medium">Reject</span>
                    </button>
                  </div>
                  {existingComments.length === 0 && (
                    <p className="text-xs text-red-600 mt-2">⚠️ Please add at least one comment before submitting your decision</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    onClick={handleReview}
                    disabled={!reviewAction || submitting || existingComments.length === 0}
                    className={`flex-1 py-3 rounded-lg font-semibold ${
                      reviewAction === 'approve'
                        ? 'bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed'
                        : reviewAction === 'reject'
                        ? 'bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {submitting ? 'Submitting...' : `Submit ${reviewAction === 'approve' ? 'Approval' : reviewAction === 'reject' ? 'Rejection' : 'Review'}`}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedOpinion(null);
                      setReviewAction('');
                      setNewComment('');
                      setExistingComments([]);
                    }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewDashboard;
